/**
 * Wave 29 Chunk 7 — /team/attendance Register page.
 *
 * Full attendance history for HR (and own-only for tech). Filters by date
 * range, user, kind, out-of-zone, and overridden. HR can override any event
 * with a mandatory reason — every override is audit-logged server-side.
 *
 * Wave 27.3 (2026-02-20, Dirk):
 *   • "Today at a glance" panel pinned at the top so HR can see who's
 *     clocked in / not yet / on break / out without scrolling.
 *   • Mobile responsive — the 7-column <table> used to wrap letter-by-
 *     letter on phones. Now collapses to a stacked card list under sm:
 *     and keeps the dense table view at sm+.
 */
import { useEffect, useState, useCallback, useMemo, useRef } from "react";
import { Link } from "react-router-dom";
import {
  ChevronLeft, Clock, Filter, Pencil, AlertTriangle, ShieldCheck, MapPin,
  Coffee, LogIn, LogOut, CheckCircle2, Circle, MessageCircle,
  Download, ChevronDown, Loader2,
} from "lucide-react";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "../components/ui/sonner";
import DatePickerInput from "../components/DatePickerInput";
import DateTimePicker from "../components/DateTimePicker";
import SearchableSelect from "../components/SearchableSelect";
import SearchableMultiSelect from "../components/SearchableMultiSelect";
import WeekReconciliationCard from "../components/team/WeekReconciliationCard";
import DayEventsReviewDrawer from "../components/DayEventsReviewDrawer";
import AdminClaimsQueue from "../components/clock/AdminClaimsQueue";
import { fmtDateTimeShort } from "../lib/datetime";
import DictTextarea from "../components/DictTextarea";

const KIND_LABELS = {
  in: "Clock in", out: "Clock out", break_start: "Break start", break_end: "Break end",
};

const KIND_PILL = {
  in:          "bg-emerald-500/15 text-emerald-300 border border-emerald-500/30",
  out:         "bg-amber-500/15  text-amber-300  border border-amber-500/30",
  break_start: "bg-sky-500/15    text-sky-300    border border-sky-500/30",
  break_end:   "bg-violet-500/15 text-violet-300 border border-violet-500/30",
};

// Derive a per-user status from their day's events. Latest event wins.
// Status keys: clocked_in | on_break | clocked_out | not_yet
function deriveStatusToday(events) {
  if (!events.length) return { key: "not_yet", at: null };
  const latest = events[events.length - 1];
  let key = "not_yet";
  if (latest.kind === "in") key = "clocked_in";
  else if (latest.kind === "out") key = "clocked_out";
  else if (latest.kind === "break_start") key = "on_break";
  else if (latest.kind === "break_end") key = "clocked_in";
  return { key, at: latest.at, latest };
}

const STATUS_META = {
  clocked_in:  { label: "Clocked in",  color: "text-emerald-300", bg: "bg-emerald-500/10 border-emerald-500/30", Icon: LogIn },
  on_break:    { label: "On break",    color: "text-sky-300",     bg: "bg-sky-500/10 border-sky-500/30",         Icon: Coffee },
  clocked_out: { label: "Clocked out", color: "text-amber-300",   bg: "bg-amber-500/10 border-amber-500/30",     Icon: LogOut },
  not_yet:     { label: "Not yet",     color: "text-[#9aa3b5]",   bg: "bg-white/[0.03] border-white/10",         Icon: Circle },
};

// Wave 27.3 — Normalise a SA phone to E.164 digits (no leading + / spaces)
// suitable for the wa.me deep link. Accepts: 0721234567, +27721234567,
// 27 72 123 4567, etc. Returns "" if it can't make sense of the input.
function normalisePhoneE164ZA(raw) {
  if (!raw) return "";
  const digits = String(raw).replace(/[^\d]/g, "");
  if (!digits) return "";
  if (digits.startsWith("27") && digits.length >= 11) return digits;        // already 27...
  if (digits.startsWith("0") && digits.length === 10) return "27" + digits.slice(1); // 07X → 27X
  if (digits.length === 9) return "27" + digits;                             // 7X... missing leading 0
  return digits;                                                              // assume already international
}

// Wave 27.5 — Nudge button. PRIMARY action is a server-side push
// notification (works on every user immediately, no WhatsApp setup
// needed). If the user also has a phone number on file, we open
// WhatsApp afterward with a pre-typed message so HR can optionally
// follow up. Once Meta WhatsApp Business API is approved, the server
// will ALSO auto-send a WhatsApp template — no UI change needed.
function NudgeWhatsAppLink({ user }) {
  const [sending, setSending] = useState(false);
  const phone = normalisePhoneE164ZA(user.whatsapp || user.phone || user.mobile || user.phone_number);
  const firstName = (user.name || user.email || "team").split(" ")[0];
  const message = encodeURIComponent(
    `Hi ${firstName}, you haven't clocked in yet today on SalesFlow. ` +
    "Everything OK? Please clock in when you're on shift, or reply if you're on leave / running late."
  );

  const onClick = async (e) => {
    e.preventDefault();
    if (sending) return;
    setSending(true);
    try {
      await api.post("/team/attendance/nudge", {
        user_id: user.id,
        reason: "haven't clocked in yet today",
      });
      toast.success(`Push sent to ${user.name || "user"}`);
    } catch (err) {
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail;
      if (status === 429) {
        toast.info(detail || "Already nudged this person recently.");
      } else {
        toast.error(detail || "Could not send push.");
      }
    } finally {
      setSending(false);
    }
    // After the push, optionally open WhatsApp so HR can follow up
    // manually. Skip if no number on file.
    if (phone) {
      const href = `https://wa.me/${phone}?text=${message}`;
      window.open(href, "_blank", "noopener,noreferrer");
    }
  };

  return (
    <button
      onClick={onClick}
      disabled={sending}
      className="text-[10px] uppercase tracking-wider text-[#1FB6C1] hover:underline shrink-0 inline-flex items-center gap-1 disabled:opacity-50"
      title={phone ? "Send push + open WhatsApp" : "Send push notification"}
      data-testid={`today-nudge-${user.id}`}
    >
      <MessageCircle className="w-3 h-3" /> {sending ? "Sending…" : "Nudge"}
    </button>
  );
}

function TodayAtAGlance({ users, todayEvents }) {
  // Group events by user_id.
  const byUser = useMemo(() => {
    const m = new Map();
    for (const ev of todayEvents) {
      const arr = m.get(ev.user_id) || [];
      arr.push(ev);
      m.set(ev.user_id, arr);
    }
    for (const arr of m.values()) arr.sort((a, b) => a.at.localeCompare(b.at));
    return m;
  }, [todayEvents]);

  // Build per-user rows with status, sorted: clocked_in → on_break → clocked_out → not_yet,
  // and within each group by earliest clock-in time so the "Early Bird" is on top.
  const rows = useMemo(() => {
    const r = users.map((u) => {
      const evs = byUser.get(u.id) || [];
      const status = deriveStatusToday(evs);
      const firstIn = evs.find((e) => e.kind === "in");
      return { user: u, status, firstIn };
    });
    const order = { clocked_in: 0, on_break: 1, clocked_out: 2, not_yet: 3 };
    r.sort((a, b) => {
      const oa = order[a.status.key], ob = order[b.status.key];
      if (oa !== ob) return oa - ob;
      const ta = a.firstIn?.at || "z";
      const tb = b.firstIn?.at || "z";
      return ta.localeCompare(tb);
    });
    return r;
  }, [users, byUser]);

  const counts = useMemo(() => {
    const c = { clocked_in: 0, on_break: 0, clocked_out: 0, not_yet: 0 };
    for (const row of rows) c[row.status.key] += 1;
    return c;
  }, [rows]);

  if (!users.length) return null;

  return (
    <div className="hv-card p-4 sm:p-5 space-y-4" data-testid="today-glance">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-full bg-gradient-to-br from-[#1FB6C1] to-[#A78BFA] text-[#0a0b0e] flex items-center justify-center shrink-0 shadow-[0_0_14px_rgba(31,182,193,0.3)]">
            <Clock className="w-4 h-4" />
          </div>
          <div>
            <div className="font-display text-base font-bold text-white">Today at a glance</div>
            <div className="text-[11px] text-[#9aa3b5]">{rows.length} team member{rows.length === 1 ? "" : "s"} · live</div>
          </div>
        </div>
        <div className="flex items-center gap-1.5 flex-wrap text-[11px]" data-testid="today-counts">
          {[
            { k: "clocked_in", n: counts.clocked_in },
            { k: "on_break",   n: counts.on_break },
            { k: "clocked_out", n: counts.clocked_out },
            { k: "not_yet",    n: counts.not_yet },
          ].map(({ k, n }) => {
            const meta = STATUS_META[k];
            return (
              <span
                key={k}
                className={`inline-flex items-center gap-1 px-2 py-1 rounded-full ${meta.bg} ${meta.color}`}
                data-testid={`today-count-${k}`}
              >
                <meta.Icon className="w-3 h-3" /> {n} {meta.label.toLowerCase()}
              </span>
            );
          })}
        </div>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
        {rows.map(({ user, status, firstIn }) => {
          const meta = STATUS_META[status.key];
          const timeLabel = status.at
            ? new Date(status.at).toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit", timeZone: "Africa/Johannesburg" })
            : null;
          return (
            <div
              key={user.id}
              className={`flex items-center gap-3 p-2.5 rounded-md border ${meta.bg}`}
              data-testid={`today-row-${user.id}`}
            >
              <div className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 ${meta.color} bg-black/20`}>
                <meta.Icon className="w-4 h-4" />
              </div>
              <div className="min-w-0 flex-1">
                <div className="text-sm text-white truncate font-medium">{user.name || user.email}</div>
                <div className={`text-[11px] ${meta.color} truncate`}>
                  {meta.label}{timeLabel ? ` · ${timeLabel}` : ""}
                  {firstIn?.out_of_zone && status.key === "clocked_in" ? (
                    <span className="text-rose-300 ml-1.5 inline-flex items-center gap-0.5">
                      <AlertTriangle className="w-3 h-3" /> off-site
                    </span>
                  ) : null}
                </div>
              </div>
              {status.key === "not_yet" ? (
                <NudgeWhatsAppLink user={user} />
              ) : null}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// Card-style row used on mobile (< sm). Each event becomes a tile.
function MobileEventCard({ r, isHr }) {
  const KindPill = (
    <span className={`text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded ${KIND_PILL[r.kind] || "bg-white/[0.06] text-[#a0aabf]"}`}>
      {KIND_LABELS[r.kind] || r.kind}
    </span>
  );
  return (
    <div className="border-b border-white/[0.05] p-3 space-y-1.5" data-testid={`register-row-mobile-${r.id}`}>
      <div className="flex items-center justify-between gap-2">
        <div className="text-sm text-white font-medium truncate">{r.user_name || r.user_id?.slice(0, 8)}</div>
        {KindPill}
      </div>
      <div className="flex items-center justify-between gap-2 text-[11px]">
        <span className="text-[#a0aabf] font-mono">{fmtDateTimeShort(r.at)}</span>
        {r.out_of_zone ? (
          <span className="text-rose-300 inline-flex items-center gap-0.5">
            <AlertTriangle className="w-3 h-3" /> off-site
          </span>
        ) : r.inside ? (
          <span className="text-emerald-300 inline-flex items-center gap-0.5 truncate max-w-[60%]" title={r.inside}>
            <ShieldCheck className="w-3 h-3 shrink-0" /> <span className="truncate">{r.inside}</span>
          </span>
        ) : (
          <span className="text-[#647087]">no zone</span>
        )}
      </div>
      {(r.note || r.override_reason || (r.lat != null && r.lng != null)) && (
        <div className="flex items-center gap-3 flex-wrap text-[11px]">
          {r.lat != null && r.lng != null && (
            <a
              href={`https://www.google.com/maps?q=${r.lat},${r.lng}`}
              target="_blank" rel="noopener noreferrer"
              className="text-[#1FB6C1] inline-flex items-center gap-0.5"
              data-testid={`register-map-${r.id}`}
            >
              <MapPin className="w-3 h-3" /> map
              {r.accuracy_m != null && <span className="text-[#647087] ml-0.5">±{Math.round(r.accuracy_m)}m</span>}
            </a>
          )}
          {r.note && <span className="text-[#a0aabf] truncate flex-1">{r.note}</span>}
          {r.override_reason && (
            <span className="text-amber-300 inline-flex items-center gap-0.5" title={r.override_reason}>
              <Pencil className="w-3 h-3" /> overridden
            </span>
          )}
        </div>
      )}
      {isHr && (
        <div className="pt-1">
          <Link
            to={`/team/attendance/${r.id}/override`}
            className="text-[11px] text-[#1FB6C1] hover:underline"
            data-testid={`register-edit-${r.id}`}
          >
            Override
          </Link>
        </div>
      )}
    </div>
  );
}

export default function AttendanceRegister() {
  const { user } = useAuth();
  const isHr = user && ["owner", "admin"].includes(user.role);
  const [rows, setRows] = useState([]);
  const [users, setUsers] = useState([]);
  const [todayEvents, setTodayEvents] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [userIds, setUserIds] = useState([]);
  const userIdCsv = userIds.join(",");
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const [kind, setKind] = useState("");
  const [onlyOOZ, setOnlyOOZ] = useState(false);
  const [onlyOver, setOnlyOver] = useState(false);
  // [Iter 355 — 2026-02-26] Hours-worked summary. Loads in parallel
  // with the events register so the same page shows attendance +
  // hours split by OT type in one place (per user directive).
  const [hoursRows, setHoursRows] = useState([]);
  const [hoursTotal, setHoursTotal] = useState(null);
  const [hoursLoading, setHoursLoading] = useState(false);
  // [Iter 356 — 2026-02-26] Dirk: "sort it, per day and then per
  // member … show whether it clocks out as well … next column, hours
  // worked. In the next column, overtime worked. In the next column,
  // Saturday, Sunday, or holiday worked." The daily register is now
  // the PRIMARY view; raw punches are behind a toggle for admins
  // verifying an anomaly.
  const [dailyRows, setDailyRows] = useState([]);
  const [dailySubtotals, setDailySubtotals] = useState([]);
  const [dailySubtotalsBalanced, setDailySubtotalsBalanced] = useState([]);
  const [dailyWeeks, setDailyWeeks] = useState([]);
  const [dailyTotal, setDailyTotal] = useState(null);
  const [dailyLoading, setDailyLoading] = useState(false);
  const [showRawPunches, setShowRawPunches] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { limit: 200 };
      if (userIdCsv) params.user_id = userIdCsv;
      if (start) params.start = start;
      if (end) params.end = end;
      if (kind) params.kind = kind;
      if (onlyOOZ) params.only_out_of_zone = true;
      if (onlyOver) params.only_overridden = true;
      const { data } = await api.get("/team/attendance/register", { params });
      setRows(data.items || []);
      setTotal(data.total || 0);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not load register.");
    } finally { setLoading(false); }
  }, [userIdCsv, start, end, kind, onlyOOZ, onlyOver]);

  useEffect(() => { load(); }, [load]);

  // [Iter 355] Load hours-worked summary using the SAME filters as
  // the register. Debounced by dependency change on the same params.
  const loadHours = useCallback(async () => {
    setHoursLoading(true);
    try {
      const params = {};
      if (userIdCsv) params.user_id = userIdCsv;
      if (start) params.start = start;
      if (end) params.end = end;
      const { data } = await api.get("/team/attendance/register/hours", { params });
      setHoursRows(data.rows || []);
      setHoursTotal(data.total || null);
    } catch (_e) {
      setHoursRows([]);
      setHoursTotal(null);
    } finally { setHoursLoading(false); }
  }, [userIdCsv, start, end]);
  useEffect(() => { loadHours(); }, [loadHours]);

  // [Iter 356] Fetch the daily register — primary view.
  const loadDaily = useCallback(async () => {
    setDailyLoading(true);
    try {
      const params = {};
      if (userIdCsv) params.user_id = userIdCsv;
      if (start) params.start = start;
      if (end) params.end = end;
      const { data } = await api.get("/team/attendance/register/daily", { params });
      setDailyRows(data.rows || []);
      setDailySubtotals(data.subtotals || []);
      setDailySubtotalsBalanced(data.subtotals_balanced || []);
      setDailyWeeks(data.weeks || []);
      setDailyTotal(data.total || null);
    } catch (_e) {
      setDailyRows([]); setDailySubtotals([]);
      setDailySubtotalsBalanced([]); setDailyWeeks([]);
      setDailyTotal(null);
    } finally { setDailyLoading(false); }
  }, [userIdCsv, start, end]);
  useEffect(() => { loadDaily(); }, [loadDaily]);

  // Iter 181 — refresh the register when an admin approves/rejects a
  // claim so the new event appears immediately.
  useEffect(() => {
    const onChanged = () => load();
    window.addEventListener("hv:attendance-changed", onChanged);
    return () => window.removeEventListener("hv:attendance-changed", onChanged);
  }, [load]);

  // HR-only: load eligible users + today's events for the "Today at a glance" panel.
  useEffect(() => {
    if (!isHr) return;
    let stop = false;
    const fetchAll = async () => {
      try {
        const [{ data: usersData }, { data: todayData }] = await Promise.all([
          api.get("/users"),
          api.get("/team/attendance/today"),
        ]);
        if (stop) return;
        const arr = Array.isArray(usersData) ? usersData : (usersData?.items || []);
        setUsers(arr.filter((u) => u.role !== "client" && u.is_active !== false));
        setTodayEvents(todayData.items || []);
      } catch (_e) { /* noop */ }
    };
    fetchAll();
    // Refresh "Today at a glance" every 60s — silent, no spinner.
    const iv = setInterval(fetchAll, 60000);
    return () => { stop = true; clearInterval(iv); };
  }, [isHr]);

  return (
    <div className="space-y-4" data-testid="attendance-register-page">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <Link to="/team" className="hv-btn hv-btn-ghost text-xs">
          <ChevronLeft className="w-3.5 h-3.5" /> Back
        </Link>
        <h1 className="font-display text-xl sm:text-2xl font-bold flex items-center gap-2">
          <Clock className="w-5 h-5 text-[#1FB6C1]" /> Attendance Register
        </h1>
        <span className="text-xs text-[#a0aabf]" data-testid="register-total">{total} event{total === 1 ? "" : "s"}</span>
      </div>

      {/* HR-only — pinned "Today at a glance" panel */}
      {isHr && <TodayAtAGlance users={users} todayEvents={todayEvents} />}

      {/* HR-only — pending claim amendments queue (iter 181) */}
      {isHr && <AdminClaimsQueue />}

      <div className="hv-card p-3 grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-2 items-end" data-testid="register-filters">
        {isHr && (
          <label className="text-[10px] text-[#647087] col-span-2 sm:col-span-2">
            Members
            <SearchableMultiSelect
              value={userIds}
              onChange={setUserIds}
              options={users.map((u) => ({
                id: u.id,
                label: u.name || u.email || u.id,
                sublabel: u.name && u.email ? u.email : "",
                searchTerms: [u.name, u.email].filter(Boolean),
              }))}
              placeholder="Search members…"
              allLabel="All members"
              testId="filter-user"
            />
          </label>
        )}
        <label className="text-[10px] text-[#647087]">
          From
          <DatePickerInput value={start} onChange={setStart} testId="filter-start" />
        </label>
        <label className="text-[10px] text-[#647087]">
          To
          <DatePickerInput value={end} onChange={setEnd} testId="filter-end" />
        </label>
        <label className="text-[10px] text-[#647087]">
          Kind
          <select value={kind} onChange={(e) => setKind(e.target.value)} className="hv-input text-xs w-full" data-testid="filter-kind">
            <option value="">— any —</option>
            {Object.entries(KIND_LABELS).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
        </label>
        <label className="text-[10px] text-[#647087] inline-flex items-center gap-1 mt-3">
          <input type="checkbox" checked={onlyOOZ} onChange={(e) => setOnlyOOZ(e.target.checked)} data-testid="filter-ooz" />
          Off-site only
        </label>
        <label className="text-[10px] text-[#647087] inline-flex items-center gap-1 mt-3">
          <input type="checkbox" checked={onlyOver} onChange={(e) => setOnlyOver(e.target.checked)} data-testid="filter-overridden" />
          Overridden only
        </label>
        <button onClick={() => { setUserIds([]); setStart(""); setEnd(""); setKind(""); setOnlyOOZ(false); setOnlyOver(false); }} className="hv-btn hv-btn-ghost text-xs col-span-2 sm:col-span-1" data-testid="filter-clear">
          <Filter className="w-3.5 h-3.5" /> Clear
        </button>
        <ExportButton
          filters={{ user_id: userIdCsv, start, end, kind, only_out_of_zone: onlyOOZ, only_overridden: onlyOver }}
        />
      </div>

      {/* [Iter 356 — 2026-02-26] PRIMARY view — per-member-per-day
          register with totalizers. Raw punches sit behind a toggle. */}
      <DailyRegisterTable
        rows={dailyRows}
        subtotals={dailySubtotals}
        subtotalsBalanced={dailySubtotalsBalanced}
        total={dailyTotal}
        loading={dailyLoading}
        isHr={isHr}
        onOverridden={loadDaily}
      />

      {/* Iter 372.p3 — Week reconciliation card for weekly-balance /
          short-hours prompt policy. */}
      <WeekReconciliationCard
        weeks={dailyWeeks}
        isHr={isHr}
        onChanged={loadDaily}
      />

      {/* [Iter 355] Hours worked summary — kept as an "at-a-glance"
          top block since it aggregates across all members. */}
      <HoursWorkedPanel
        rows={hoursRows}
        total={hoursTotal}
        loading={hoursLoading}
      />

      <button
        type="button"
        onClick={() => setShowRawPunches((v) => !v)}
        className="text-[11px] text-[#647087] hover:text-white underline underline-offset-2"
        data-testid="toggle-raw-punches"
      >
        {showRawPunches ? "Hide" : "Show"} raw punches (for verification)
      </button>

      {showRawPunches && (
        loading ? (
          <div className="h-32 hv-card animate-pulse" />
        ) : rows.length === 0 ? (
          <div className="hv-card p-8 text-center text-sm text-[#647087]">No events match.</div>
        ) : (
          <>
            {/* Mobile (< sm) — stacked card list */}
            <div className="hv-card sm:hidden" data-testid="register-list-mobile">
              {rows.map((r) => <MobileEventCard key={r.id} r={r} isHr={isHr} />)}
            </div>
          {/* Desktop (sm+) — keep the dense table */}
          <div className="hv-card overflow-x-auto hidden sm:block" data-testid="register-table">
            <table className="w-full text-xs">
              <thead className="text-[10px] uppercase tracking-wider text-[#647087] border-b border-white/5">
                <tr>
                  <th className="text-left p-2">When</th>
                  <th className="text-left p-2">Who</th>
                  <th className="text-left p-2">Kind</th>
                  <th className="text-left p-2">Zone</th>
                  <th className="text-left p-2">Note</th>
                  <th className="text-left p-2">Override</th>
                  {isHr && <th className="text-right p-2">Actions</th>}
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.id} className="border-b border-white/[0.04] hover:bg-white/[0.02]" data-testid={`register-row-${r.id}`}>
                    <td className="p-2 whitespace-nowrap font-mono">{fmtDateTimeShort(r.at)}</td>
                    <td className="p-2 truncate max-w-[160px]">{r.user_name || r.user_id?.slice(0, 8)}</td>
                    <td className="p-2">
                      <span className={`text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded ${KIND_PILL[r.kind] || "bg-white/[0.06] text-[#a0aabf]"}`}>
                        {KIND_LABELS[r.kind] || r.kind}
                      </span>
                    </td>
                    <td className="p-2">
                      <div className="flex items-center gap-1 flex-wrap">
                        {r.out_of_zone ? (
                          <span className="text-[10px] text-red-300 inline-flex items-center gap-0.5"><AlertTriangle className="w-3 h-3" /> off-site</span>
                        ) : r.inside ? (
                          <span className="text-[10px] text-emerald-300 inline-flex items-center gap-0.5"><ShieldCheck className="w-3 h-3" /> {r.inside}</span>
                        ) : (
                          <span className="text-[10px] text-[#647087]">—</span>
                        )}
                        {r.lat != null && r.lng != null && (
                          <a
                            href={`https://www.google.com/maps?q=${r.lat},${r.lng}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-[10px] text-[#1FB6C1] inline-flex items-center gap-0.5 hover:underline"
                            title={`Verify location on Google Maps — accuracy ±${r.accuracy_m ?? "?"} m`}
                            data-testid={`register-map-desktop-${r.id}`}
                          >
                            <MapPin className="w-3 h-3" /> map
                            {r.accuracy_m != null && (
                              <span className="text-[#647087] ml-0.5">±{Math.round(r.accuracy_m)}m</span>
                            )}
                          </a>
                        )}
                      </div>
                    </td>
                    <td className="p-2 truncate max-w-[180px]" title={r.note || ""}>{r.note || <span className="text-[#475063]">—</span>}</td>
                    <td className="p-2">
                      {r.override_reason ? (
                        <span className="text-[10px] text-amber-300 inline-flex items-center gap-0.5" title={`${r.override_reason} (by ${r.overridden_by_name})`}>
                          <Pencil className="w-3 h-3" /> {r.overridden_by_name}
                        </span>
                      ) : (
                        <span className="text-[10px] text-[#475063]">—</span>
                      )}
                    </td>
                    {isHr && (
                      <td className="p-2 text-right">
                        <Link
                          to={`/team/attendance/${r.id}/override`}
                          className="text-[10px] text-[#1FB6C1] hover:underline"
                          data-testid={`register-edit-desktop-${r.id}`}
                        >
                          Override
                        </Link>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          </>
        )
      )}
    </div>
  );
}

/**
 * <ExportButton /> — Iter 354 (2026-02-26).
 *
 * Dirk: "Add an export to cvs/xls/pdf option to attendance register
 * with filters for days, members etc."
 *
 * Compact dropdown that offers CSV, Excel and PDF exports of the
 * currently-filtered attendance register. Uses the same filter state
 * as the on-screen list — "what you see is what you export".
 *
 * Fetches the file as a Blob through the authenticated axios instance
 * (so Bearer + tenant scoping ride along), then drives an in-place
 * `<a download>` click. Never opens a new tab (would break the PWA
 * on iOS + strip auth headers).
 */
function ExportButton({ filters }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const wrapRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("touchstart", onDoc);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("touchstart", onDoc);
    };
  }, [open]);

  const run = async (format) => {
    setBusy(true);
    setOpen(false);
    try {
      const params = { format };
      Object.entries(filters || {}).forEach(([k, v]) => {
        if (v === "" || v === false || v == null) return;
        params[k] = v;
      });
      const resp = await api.get("/team/attendance/register/export", {
        params,
        responseType: "blob",
      });
      // Content-Disposition header carries the friendly filename.
      const cd = resp.headers?.["content-disposition"] || resp.headers?.["Content-Disposition"] || "";
      const m = /filename="?([^";]+)"?/i.exec(cd);
      const filename = m?.[1] || `attendance.${format === "xlsx" ? "xlsx" : format}`;
      const url = URL.createObjectURL(resp.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      a.rel = "noopener";
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 15_000);
      toast.success(`Exported ${filename}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Export failed.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="relative col-span-2 sm:col-span-1" ref={wrapRef} data-testid="attendance-export">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        disabled={busy}
        className="hv-btn hv-btn-ghost text-xs w-full inline-flex items-center justify-center gap-1"
        data-testid="attendance-export-trigger"
        aria-haspopup="menu"
        aria-expanded={open}
      >
        {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Download className="w-3.5 h-3.5" />}
        Export
        <ChevronDown className="w-3 h-3 opacity-70" />
      </button>
      {open && (
        <div
          className="absolute right-0 mt-1 z-20 min-w-[10rem] rounded-md border border-white/10 bg-[#0f1116] shadow-xl overflow-hidden"
          role="menu"
          data-testid="attendance-export-menu"
        >
          {[
            { fmt: "csv", label: "CSV (.csv)" },
            { fmt: "xlsx", label: "Excel (.xlsx)" },
            { fmt: "pdf", label: "PDF (.pdf)" },
          ].map(({ fmt, label }) => (
            <button
              key={fmt}
              type="button"
              onClick={() => run(fmt)}
              className="block w-full text-left px-3 py-2 text-xs text-[#dde3ec] hover:bg-white/5"
              data-testid={`attendance-export-${fmt}`}
            >
              {label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}


/**
 * <HoursWorkedPanel /> — Iter 355 (2026-02-26).
 *
 * Dirk: "it must be split ons the report per overtime type … show
 * attendance + hours worked all in one" (as part of the attendance
 * register).
 *
 * Renders a compact table of per-user hours worked split by pay
 * bucket:
 *   • Ordinary (within the weekly threshold on working weekdays)
 *   • Weekday OT (past the weekly threshold, default 1.5×)
 *   • Saturday (default 1.5×)
 *   • Sunday (default 2×)
 *   • Public holiday (default 2×)
 *   • Equivalent 1× minutes (each bucket × its multiplier — the
 *     figure payroll actually bills)
 *
 * Each user row's config is fed by the backend, so a user with a
 * customised multiplier (e.g. saturday 1.75×) shows the correct
 * equivalent total without any frontend calculation.
 *
 * Uses the SAME filter state as the events table above (via
 * `useEffect` in the parent) so filtering by date range / user
 * updates both panels in sync.
 */
function HoursWorkedPanel({ rows, total, loading }) {
  if (loading) {
    return (
      <div className="hv-card p-4 animate-pulse text-xs text-[#647087]" data-testid="hours-panel-loading">
        Calculating hours worked…
      </div>
    );
  }
  if (!rows || rows.length === 0) {
    return null;
  }
  const fmt = (m) => {
    if (!m) return "0";
    const h = Math.floor(m / 60);
    const mm = m % 60;
    return mm === 0 ? `${h}h` : `${h}h ${mm}m`;
  };
  const mulLabel = (cfg, key, fallback) => {
    const v = cfg?.[key];
    if (v == null) return `${fallback}×`;
    // "1.5×" (drop trailing zeros).
    const s = Number(v).toFixed(2).replace(/\.?0+$/, "");
    return `${s}×`;
  };
  return (
    <div className="hv-card p-3" data-testid="hours-panel">
      <div className="flex items-center justify-between mb-2">
        <h3 className="font-display text-sm font-bold text-white">Hours worked (by pay bucket)</h3>
        <span className="text-[10px] text-[#647087]">
          Filters above apply · multipliers per member setup
        </span>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-xs" data-testid="hours-panel-table">
          <thead className="text-[10px] uppercase tracking-wider text-[#647087] border-b border-white/5">
            <tr>
              <th className="text-left p-2">Who</th>
              <th className="text-right p-2">Ordinary (1×)</th>
              <th className="text-right p-2">Weekday OT</th>
              <th className="text-right p-2">Saturday</th>
              <th className="text-right p-2">Sunday</th>
              <th className="text-right p-2">Holiday</th>
              <th className="text-right p-2">Equivalent (1×)</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.user_id} className="border-b border-white/5" data-testid={`hours-row-${r.user_id}`}>
                <td className="p-2 text-white">{r.user_name || r.user_id.slice(0, 8)}</td>
                <td className="p-2 text-right tabular-nums">{fmt(r.ordinary_min)}</td>
                <td className="p-2 text-right tabular-nums">
                  {fmt(r.overtime_min)}
                  {r.overtime_min > 0 && (
                    <span className="ml-1 text-[9px] text-[#647087]">
                      {mulLabel(r.config, "weekday_ot_mul", "1.5")}
                    </span>
                  )}
                </td>
                <td className="p-2 text-right tabular-nums">
                  {fmt(r.saturday_min)}
                  {r.saturday_min > 0 && (
                    <span className="ml-1 text-[9px] text-[#647087]">
                      {mulLabel(r.config, "saturday_mul", "1.5")}
                    </span>
                  )}
                </td>
                <td className="p-2 text-right tabular-nums">
                  {fmt(r.sunday_min)}
                  {r.sunday_min > 0 && (
                    <span className="ml-1 text-[9px] text-[#647087]">
                      {mulLabel(r.config, "sunday_mul", "2")}
                    </span>
                  )}
                </td>
                <td className="p-2 text-right tabular-nums">
                  {fmt(r.holiday_min)}
                  {r.holiday_min > 0 && (
                    <span className="ml-1 text-[9px] text-[#647087]">
                      {mulLabel(r.config, "holiday_mul", "2")}
                    </span>
                  )}
                </td>
                <td className="p-2 text-right tabular-nums font-bold text-[#1FB6C1]">
                  {fmt(r.equivalent_ordinary_min)}
                </td>
              </tr>
            ))}
          </tbody>
          {total && rows.length > 1 && (
            <tfoot className="border-t border-white/10 font-bold">
              <tr data-testid="hours-total">
                <td className="p-2">Total</td>
                <td className="p-2 text-right tabular-nums">{fmt(total.ordinary_min)}</td>
                <td className="p-2 text-right tabular-nums">{fmt(total.overtime_min)}</td>
                <td className="p-2 text-right tabular-nums">{fmt(total.saturday_min)}</td>
                <td className="p-2 text-right tabular-nums">{fmt(total.sunday_min)}</td>
                <td className="p-2 text-right tabular-nums">{fmt(total.holiday_min)}</td>
                <td className="p-2 text-right tabular-nums text-[#1FB6C1]">{fmt(total.equivalent_ordinary_min)}</td>
              </tr>
            </tfoot>
          )}
        </table>
      </div>
    </div>
  );
}


/**
 * <DailyRegisterTable /> — Iter 356 (2026-02-26) · rebuilt Iter 358.
 *
 * Dirk (Iter 358): "if you select a week and you select one user only,
 * then it's nice … but when we select multiple users, then it has to
 * be sure to only show the subtotal per user separately at the bottom,
 * not after each line". Rows are now grouped strictly by day (all
 * members under each date), with per-member subtotals collected into a
 * single "Totals per member" table at the bottom, followed by the
 * grand total. Single-user filter still shows a compact subtotal row
 * because that's genuinely useful there.
 *
 * One row per (member × day). Columns: Date, Member, Day-kind, In,
 * Out, Hours, Overtime, Saturday, Sunday, Holiday, Equivalent (1×).
 */
function DailyRegisterTable({ rows, subtotals, subtotalsBalanced, total, loading, isHr, onOverridden }) {
  // Iter 372 — inline edit modal handle
  const [editEvent, setEditEvent] = useState(null); // {eid, at, kind, label}
  // Iter 372.p6 — Day events review drawer state
  const [reviewRow, setReviewRow] = useState(null);
  // Iter 372.p1 — Local overlay of freshly-recomputed rows from the
  // override endpoint response so the table updates INSTANTLY without
  // waiting for a full register refetch. Key = `${date}::${user_id}`.
  // Also tracks which rows just got patched for a brief highlight.
  const [rowsPatch, setRowsPatch] = useState({});
  const [flashed, setFlashed] = useState({});
  // Reset overlay whenever the underlying rows prop changes (a full
  // refetch already carries the fresh numbers).
  useEffect(() => { setRowsPatch({}); }, [rows]);

  const applyRecomputedRows = useCallback((recomputed) => {
    if (!Array.isArray(recomputed) || recomputed.length === 0) return;
    setRowsPatch((prev) => {
      const next = { ...prev };
      recomputed.forEach((r) => {
        const key = `${r.date}::${r.user_id}`;
        next[key] = r;
      });
      return next;
    });
    // Flash the updated rows for ~1.6s so HR sees WHAT just changed.
    const flashKeys = {};
    recomputed.forEach((r) => { flashKeys[`${r.date}::${r.user_id}`] = Date.now(); });
    setFlashed((prev) => ({ ...prev, ...flashKeys }));
    setTimeout(() => {
      setFlashed((prev) => {
        const next = { ...prev };
        Object.keys(flashKeys).forEach((k) => { delete next[k]; });
        return next;
      });
    }, 1600);
  }, []);

  if (loading) {
    return (
      <div className="hv-card p-4 animate-pulse text-xs text-[#647087]" data-testid="daily-register-loading">
        Building daily register…
      </div>
    );
  }
  if (!rows || rows.length === 0) {
    return (
      <div className="hv-card p-8 text-center text-sm text-[#647087]" data-testid="daily-register-empty">
        No attendance in this range.
      </div>
    );
  }
  const fmt = (m) => {
    if (!m) return "—";
    const h = Math.floor(m / 60);
    const mm = m % 60;
    return mm === 0 ? `${h}h` : `${h}h${mm.toString().padStart(2, "0")}`;
  };
  const fmtTime = (iso) => {
    if (!iso) return "";
    try {
      return new Date(iso).toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" });
    } catch { return ""; }
  };
  const weekdayName = (iso) => {
    try {
      return new Date(iso + "T00:00:00").toLocaleDateString("en-ZA", { weekday: "short" });
    } catch { return ""; }
  };
  const kindPill = (k) => {
    if (k === "public_holiday") return <span className="text-[9px] px-1.5 py-0.5 rounded bg-rose-500/15 text-rose-300 border border-rose-400/30">HOL</span>;
    if (k === "sunday") return <span className="text-[9px] px-1.5 py-0.5 rounded bg-amber-500/15 text-amber-300 border border-amber-400/30">SUN</span>;
    if (k === "saturday") return <span className="text-[9px] px-1.5 py-0.5 rounded bg-sky-500/15 text-sky-300 border border-sky-400/30">SAT</span>;
    return null;
  };
  const fmtDate = (iso) => {
    try {
      return new Date(iso + "T00:00:00").toLocaleDateString("en-ZA", {
        weekday: "short", day: "2-digit", month: "short", year: "numeric",
      });
    } catch { return iso; }
  };
  const openMap = (lat, lng) => {
    if (lat == null || lng == null) return;
    window.open(`https://www.google.com/maps?q=${lat},${lng}`, "_blank", "noopener");
  };
  const startOverride = (row, which) => {
    const evt = which === "in" ? row.in_event : row.out_event;
    setEditEvent({
      eid: evt?.id,
      at: (which === "in" ? row.in_at : row.out_at) || "",
      kind: which,
      user_name: row.user_name,
      date: row.date,
    });
  };

  const singleUserView = subtotals && subtotals.length === 1;
  // Iter 372.p1 — Overlay the local patch map on top of props.rows so
  // freshly-recomputed rows render immediately after a save. Cleared
  // rows (tombstones from the backend) are filtered out entirely.
  const effectiveRows = (rows || [])
    .map((r) => {
      const key = `${r.date}::${r.user_id}`;
      const patch = rowsPatch[key];
      if (!patch) return r;
      if (patch.cleared) return null;
      return patch;
    })
    .filter(Boolean);
  const rowsByDate = [];
  let prevDate = null;
  const COL_COUNT = isHr ? 14 : 13;
  effectiveRows.forEach((r) => {
    if (r.date !== prevDate) {
      rowsByDate.push(
        <tr key={`hdr-${r.date}`} className="bg-white/[0.04] border-t border-white/10" data-testid={`daily-date-header-${r.date}`}>
          <td colSpan={COL_COUNT} className="px-2 py-1.5 text-[10px] uppercase tracking-wider text-[#a0e6ea] font-semibold">
            {fmtDate(r.date)}
          </td>
        </tr>
      );
      prevDate = r.date;
    }
    const flashKey = `${r.date}::${r.user_id}`;
    const isFlashing = !!flashed[flashKey];
    rowsByDate.push(
      <tr
        key={`${r.date}-${r.user_id}`}
        className={`border-b border-white/[0.04] hover:bg-white/[0.02] transition-colors ${isFlashing ? "bg-emerald-500/10" : ""}`}
        data-testid={`daily-row-${r.date}-${r.user_id}`}
      >
        <td className="p-2 whitespace-nowrap font-mono text-white/60 text-[10px]">{r.date}</td>
        <td className="p-2 truncate max-w-[180px] text-white/90">
          <span className="inline-flex items-center gap-1">
            <span className="truncate">{r.user_name || r.user_id.slice(0, 8)}</span>
            {r.has_dangling_break && (
              <button
                type="button"
                title="Employee opened a break but never clocked back from it. Click to review events."
                className="inline-flex items-center justify-center w-4 h-4 rounded-full bg-amber-500/20 text-amber-300 border border-amber-500/30 text-[10px] font-bold hover:bg-amber-500/30"
                data-testid={`daily-dangling-break-${r.date}-${r.user_id}`}
                onClick={() => setReviewRow(r)}
              >
                !
              </button>
            )}
            {r.has_multiple_out && (
              <button
                type="button"
                title="Multiple OUT punches detected. Click to review and clean up stray events."
                className="inline-flex items-center justify-center w-4 h-4 rounded-full bg-sky-500/20 text-sky-300 border border-sky-500/30 text-[10px] font-bold hover:bg-sky-500/30"
                data-testid={`daily-multi-out-${r.date}-${r.user_id}`}
                onClick={() => setReviewRow(r)}
              >
                ⟳
              </button>
            )}
            {r.no_clock && (
              <span
                title="No clock events on this scheduled workday."
                className="inline-flex items-center gap-0.5 px-1 py-0 rounded bg-rose-500/15 text-rose-300 border border-rose-500/30 text-[9px] uppercase tracking-wider"
                data-testid={`daily-no-clock-${r.date}-${r.user_id}`}
              >
                No clock
              </span>
            )}
            {r.leave_kind && (
              <span
                title={`Approved ${r.leave_kind} leave`}
                className="inline-flex items-center gap-0.5 px-1 py-0 rounded bg-indigo-500/15 text-indigo-300 border border-indigo-500/30 text-[9px] uppercase tracking-wider"
                data-testid={`daily-leave-${r.date}-${r.user_id}`}
              >
                {r.leave_kind}
              </span>
            )}
            {r.is_public_holiday && (
              <span
                title={r.public_holiday_name
                  ? `Public holiday: ${r.public_holiday_name}. Paid at ordinary rate (BCEA)`
                  : "Public holiday · paid at ordinary rate (BCEA)"}
                className="inline-flex items-center gap-0.5 px-1 py-0 rounded bg-fuchsia-500/15 text-fuchsia-300 border border-fuchsia-500/30 text-[9px] uppercase tracking-wider"
                data-testid={`daily-public-holiday-${r.date}-${r.user_id}`}
              >
                PH
              </span>
            )}
          </span>
        </td>
        <td className="p-2 whitespace-nowrap text-white/70 text-[11px]" data-testid={`daily-weekday-${r.date}-${r.user_id}`}>
          <span className="mr-1">{weekdayName(r.date)}</span>{kindPill(r.day_kind)}
        </td>
        <TimeCell label="In" iso={r.in_at} event={r.in_event} isHr={isHr} openMap={openMap} onOverride={() => startOverride(r, "in")} testId={`daily-in-${r.date}-${r.user_id}`} />
        {r.open
          ? <td className="p-2 text-amber-300 text-[10px] font-mono">OPEN</td>
          : <TimeCell label="Out" iso={r.out_at} event={r.out_event} isHr={isHr} openMap={openMap} onOverride={() => startOverride(r, "out")} testId={`daily-out-${r.date}-${r.user_id}`} />
        }
        <td className="p-2 text-right tabular-nums text-[11px] text-[#a0aabf]" data-testid={`daily-break-${r.date}-${r.user_id}`} title={r.break_min ? `Includes ${r.break_min}min clocked break` : "Compulsory unpaid lunch (auto)"}>
          {r.lunch_min || r.break_min
            ? <span className="inline-flex items-center gap-1">
                <span className={r.break_min ? "text-emerald-300" : ""}>{fmt((r.lunch_min || 0) + (r.break_min || 0))}</span>
                {!r.break_min && r.lunch_min ? <span className="text-[9px] text-[#647087]">auto</span> : null}
              </span>
            : "—"}
        </td>
        <td className="p-2 text-right tabular-nums font-medium">{fmt(r.worked_min)}</td>
        <td className="p-2 text-right tabular-nums">{fmt(r.overtime_min)}</td>
        <td className="p-2 text-right tabular-nums">{fmt(r.saturday_min)}</td>
        <td className="p-2 text-right tabular-nums">{fmt(r.sunday_min)}</td>
        <td className="p-2 text-right tabular-nums">{fmt(r.holiday_min)}</td>
        <td className="p-2 text-right tabular-nums font-bold text-[#1FB6C1]">{fmt(r.equivalent_ordinary_min)}</td>
      </tr>
    );
  });

  return (
    <div className="space-y-3" data-testid="daily-register">
      <div className="hv-card overflow-x-auto">
        <div className="p-3 border-b border-white/5 flex items-center justify-between">
          <h3 className="font-display text-sm font-bold text-white">Daily register</h3>
          <span className="text-[10px] text-[#647087]">
            Grouped by day · one row per member · lunch auto-deducted · filters above apply
          </span>
        </div>
        <table className="w-full text-xs" data-testid="daily-register-table">
          <thead className="text-[10px] uppercase tracking-wider text-[#647087] border-b border-white/5">
            <tr>
              <th className="text-left p-2">Date</th>
              <th className="text-left p-2">Member</th>
              <th className="text-left p-2">Day</th>
              <th className="text-left p-2">In</th>
              <th className="text-left p-2">Out</th>
              <th className="text-right p-2" title="Compulsory lunch + any clocked break">Break</th>
              <th className="text-right p-2">Hours</th>
              <th className="text-right p-2">OT</th>
              <th className="text-right p-2">Sat</th>
              <th className="text-right p-2">Sun</th>
              <th className="text-right p-2">Hol</th>
              <th className="text-right p-2">Equiv (1×)</th>
            </tr>
          </thead>
          <tbody>{rowsByDate}</tbody>
        </table>
      </div>

      {/* Iter 358 — Per-member subtotals for the selected range. */}
      {subtotals && subtotals.length > 0 && (
        <div className="hv-card overflow-x-auto" data-testid="daily-per-member-totals">
          <div className="p-3 border-b border-white/5 flex items-center justify-between">
            <h3 className="font-display text-sm font-bold text-white">
              {singleUserView ? "Total for period" : "Totals per member · selected period"}
            </h3>
            <span className="text-[10px] text-[#647087]">
              Sum of hours & overtime across the filter window
            </span>
          </div>
          <table className="w-full text-xs">
            <thead className="text-[10px] uppercase tracking-wider text-[#647087] border-b border-white/5">
              <tr>
                <th className="text-left p-2">Member</th>
                <th className="text-right p-2">Hours</th>
                <th className="text-right p-2">OT</th>
                <th className="text-right p-2">Sat</th>
                <th className="text-right p-2">Sun</th>
                <th className="text-right p-2">Hol</th>
                <th className="text-right p-2">Equiv (1×)</th>
              </tr>
            </thead>
            <tbody>
              {subtotals.map((st) => {
                // Iter 372.p3 — surface balanced overtime if it
                // materially differs from gross (i.e. a same-week
                // shortfall absorbed some OT). Renders alongside the
                // gross figure so HR sees both.
                const bal = (subtotalsBalanced || []).find((b) => b.user_id === st.user_id);
                const absorbed = bal && bal.absorbed_min ? bal.absorbed_min : 0;
                const hasBalance = absorbed > 0 && bal && bal.overtime_min !== st.overtime_min;
                return (
                  <tr key={`sub-${st.user_id}`} className="border-b border-white/[0.04]" data-testid={`daily-subtotal-${st.user_id}`}>
                    <td className="p-2 text-white/90 truncate max-w-[240px]">{st.user_name || st.user_id.slice(0, 8)}</td>
                    <td className="p-2 text-right tabular-nums font-medium">{fmt(st.worked_min)}</td>
                    <td className="p-2 text-right tabular-nums">
                      {fmt(st.overtime_min)}
                      {hasBalance && (
                        <div
                          className="text-[9px] text-emerald-300 mt-0.5"
                          title={`Balanced: ${absorbed} min absorbed by same-week short-hours`}
                          data-testid={`daily-subtotal-ot-balanced-${st.user_id}`}
                        >
                          → {fmt(bal.overtime_min)} bal.
                        </div>
                      )}
                    </td>
                    <td className="p-2 text-right tabular-nums">{fmt(st.saturday_min)}</td>
                    <td className="p-2 text-right tabular-nums">{fmt(st.sunday_min)}</td>
                    <td className="p-2 text-right tabular-nums">{fmt(st.holiday_min)}</td>
                    <td className="p-2 text-right tabular-nums font-bold text-[#1FB6C1]">
                      {fmt(st.equivalent_ordinary_min)}
                      {hasBalance && (
                        <div className="text-[9px] text-emerald-300 mt-0.5">
                          → {fmt(bal.equivalent_ordinary_min)} bal.
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
            {total && (
              <tfoot className="border-t-2 border-[#1FB6C1]/40 bg-[#1FB6C1]/[0.08]">
                <tr className="font-bold" data-testid="daily-grand-total">
                  <td className="p-2 text-[10px] uppercase tracking-wider text-[#1FB6C1]">Grand total</td>
                  <td className="p-2 text-right tabular-nums">{fmt(total.worked_min)}</td>
                  <td className="p-2 text-right tabular-nums">{fmt(total.overtime_min)}</td>
                  <td className="p-2 text-right tabular-nums">{fmt(total.saturday_min)}</td>
                  <td className="p-2 text-right tabular-nums">{fmt(total.sunday_min)}</td>
                  <td className="p-2 text-right tabular-nums">{fmt(total.holiday_min)}</td>
                  <td className="p-2 text-right tabular-nums text-[#1FB6C1]">{fmt(total.equivalent_ordinary_min)}</td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      )}

      {editEvent && (
        <EditEventModal
          event={editEvent}
          onClose={() => setEditEvent(null)}
          onSaved={(recomputed) => {
            setEditEvent(null);
            // Iter 372.p1 — Splice the recomputed rows in locally so
            // HR sees fresh HOURS/OT/SAT/SUN/HOL/EQUIV instantly. If
            // the backend didn't send any (unexpected), fall back to
            // a full refetch so numbers can't stay stale.
            if (Array.isArray(recomputed) && recomputed.length > 0) {
              applyRecomputedRows(recomputed);
            } else {
              onOverridden?.();
            }
          }}
        />
      )}

      {/* Iter 372.p6 — Day events review drawer */}
      {reviewRow && (
        <DayEventsReviewDrawer
          row={reviewRow}
          onClose={() => setReviewRow(null)}
          onRowsRecomputed={(recomputed) => {
            if (Array.isArray(recomputed) && recomputed.length > 0) {
              applyRecomputedRows(recomputed);
            }
          }}
        />
      )}
    </div>
  );
}

/**
 * Iter 372 — TimeCell renders the clock-in / clock-out time with two
 * affordances: a GPS pin (opens Google Maps to the punch location) and
 * an admin override pencil (opens EditEventModal).
 */
function TimeCell({ iso, event, isHr, openMap, onOverride, testId }) {
  const fmt = (v) => {
    if (!v) return "";
    try {
      return new Date(v).toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" });
    } catch { return ""; }
  };
  if (!iso) {
    return <td className="p-2 text-[#475063]" data-testid={testId}>—</td>;
  }
  const overridden = !!event?.overridden_by_name;
  const hasCoords = event?.lat != null && event?.lng != null;
  return (
    <td className="p-2 whitespace-nowrap" data-testid={testId}>
      <div className="inline-flex items-center gap-1">
        <span className={`font-mono ${overridden ? "text-amber-300" : "text-[#c6ccd7]"}`} title={overridden ? `Overridden by ${event.overridden_by_name} on ${(event.overridden_at || "").slice(0, 16)}${event.override_reason ? " · " + event.override_reason : ""}` : ""}>
          {fmt(iso)}
        </span>
        {overridden && (
          <span className="text-[9px] px-1 rounded bg-amber-500/15 text-amber-300 border border-amber-500/30" data-testid={`${testId}-overridden`}>OVR</span>
        )}
        {hasCoords && (
          <button
            type="button"
            onClick={() => openMap(event.lat, event.lng)}
            className="text-[#647087] hover:text-[#1FB6C1] transition"
            title={`Open in Google Maps · ${event.lat.toFixed?.(5)}, ${event.lng.toFixed?.(5)}`}
            data-testid={`${testId}-map`}
          >
            <MapPin className="w-3 h-3" />
          </button>
        )}
        {isHr && event?.id && (
          <button
            type="button"
            onClick={onOverride}
            className="text-[#647087] hover:text-amber-300 transition"
            title="Override this time"
            data-testid={`${testId}-override`}
          >
            <Pencil className="w-3 h-3" />
          </button>
        )}
      </div>
    </td>
  );
}

/**
 * Iter 372 — EditEventModal is a small owner/admin-only override form.
 * PATCHes /api/team/attendance/events/{eid} with new `at` + mandatory
 * `override_reason`. Backend audit-logs every change to
 * `attendance_event_overrides` (who / when / before / after / reason).
 */
function EditEventModal({ event, onClose, onSaved }) {
  const [when, setWhen] = useState(() => {
    try {
      const d = new Date(event.at);
      const off = d.getTimezoneOffset() * 60000;
      return new Date(d.getTime() - off).toISOString().slice(0, 16);
    } catch { return ""; }
  });
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const save = async () => {
    if (!reason.trim() || reason.trim().length < 3) {
      setErr("A reason (≥3 chars) is required for the audit trail.");
      return;
    }
    if (!when) {
      setErr("Pick a corrected time.");
      return;
    }
    setBusy(true);
    setErr("");
    try {
      const localDate = new Date(when);
      const { data } = await api.patch(`/team/attendance/events/${event.eid}`, {
        at: localDate.toISOString(),
        override_reason: reason.trim(),
      });
      toast.success(`Time updated for ${event.user_name || "employee"}`);
      // Iter 372.p1 — Pass the freshly recomputed rows back to the
      // caller so the register table can splice them in without a
      // full refetch (avoids stale numbers during the round-trip).
      onSaved?.(data?.recomputed_rows || []);
    } catch (e) {
      setErr(e?.response?.data?.detail || e.message || "Override failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4" data-testid="daily-override-modal">
      <div className="hv-card w-full max-w-md p-4 space-y-3">
        <div>
          <div className="text-[10px] uppercase tracking-[0.2em] text-[#647087]">Override attendance</div>
          <h3 className="font-display text-lg font-bold text-white mt-1">
            {event.user_name} · {event.kind === "in" ? "Clock in" : "Clock out"}
          </h3>
          <div className="text-xs text-[#a0aabf] mt-0.5">{event.date}</div>
        </div>
        <label className="block">
          <span className="text-[10px] uppercase tracking-[0.2em] text-[#647087]">Corrected time</span>
          <div className="mt-1">
            <DateTimePicker
              value={when}
              onChange={setWhen}
              placeholder="Pick date & time"
              testId="daily-override-when"
              required
            />
          </div>
        </label>
        <label className="block">
          <span className="text-[10px] uppercase tracking-[0.2em] text-[#647087]">Reason (audit)</span>
          <DictTextarea
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            rows={3}
            placeholder="Why is this being overridden? e.g. 'Pressed clock-out from home instead of site — actual out was 16:45.'"
            className="hv-input w-full mt-1 text-sm"
            data-testid="daily-override-reason"
            maxLength={500}
          />
        </label>
        {err && <div className="text-xs text-rose-300" data-testid="daily-override-err">{err}</div>}
        <div className="flex items-center justify-between gap-2 pt-1">
          <button
            type="button"
            onClick={onClose}
            className="hv-btn hv-btn-ghost text-xs"
            data-testid="daily-override-cancel"
            disabled={busy}
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={save}
            className="hv-btn hv-btn-primary text-xs disabled:opacity-50"
            data-testid="daily-override-save"
            disabled={busy}
          >
            {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <ShieldCheck className="w-3 h-3" />}
            Save override
          </button>
        </div>
      </div>
    </div>
  );
}

